import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { expenseApi, adminExpenseApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useCurrentUser } from '../../hooks/useCurrentUser.jsx';
import { useFxRate } from '../../hooks/useFxRate.jsx';
import { FOREIGN_TXN_FEE_RATE } from '../../lib/currencies.js';
import { homeCurrencyFor } from '../../lib/bankLocations.js';
import { useUnsavedGuard } from '../../hooks/useUnsavedWork.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Modal from '../../components/ui/Modal.jsx';
// ReceiptScanner still mounts — it's the receipt uploader. AI field
// extraction runs server-side but all AI-specific branding and error
// surfaces have been removed from the component until we're ready to
// advertise that feature. See the "AI coming soon" banner below.
import ReceiptScanner from '../../components/expense/ReceiptScanner.jsx';
import CurrencyCombobox from '../../components/expense/CurrencyCombobox.jsx';
import { ArrowLeft, Save, Send, Plus, Trash2, ChevronDown, ChevronUp, Sparkles, Pencil } from 'lucide-react';

const MILEAGE_RATES = {
  'Associate': 'SAR 1.20/KM',
  'Senior Associate': 'SAR 1.20/KM',
  'Manager': 'SAR 1.50/KM',
  'Director': 'SAR 1.80/KM',
  'Partner': 'SAR 2.00/KM',
};

const EMPTY_LINE = {
  itemKey: '',
  date: '',
  expenseTypeId: '',
  description: '',
  vendorName: '',
  currencyType: 'SAR',
  currencyAmount: '',
  exchangeRate: '1',
  chargeAccount: '',
  projectPermitted: false,
};

// A fresh line item with a stable client-generated itemKey. Receipts
// uploaded for this row carry the same key so item ↔ receipt stays
// linked through the save (which re-keys line items every time).
const newLine = () => ({ ...EMPTY_LINE, itemKey: crypto.randomUUID() });

export default function NewExpenseReportPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const { user } = useCurrentUser();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(!!editId);
  const [reportId, setReportId] = useState(editId || null);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [positions, setPositions] = useState([]);
  // Teamwork project list for the Project picker. Empty = free-text
  // fallback (Teamwork not configured, or the call failed).
  const [projects, setProjects] = useState([]);
  const [lineItems, setLineItems] = useState([newLine()]);
  const [showOptional, setShowOptional] = useState(false);
  // Line item config modal — index of the row being edited, or 'new' to
  // create a new one. Editing fields directly in the table caused
  // confusion when typed amounts didn't surface anywhere obvious; the
  // modal makes "configure this item" an explicit step.
  const [editingItem, setEditingItem] = useState(null);
  const [receiptCount, setReceiptCount] = useState(0);
  // Edit mode: receipts already on the report, pre-merged with their
  // line-item data, handed to ReceiptScanner so the user can see + re-edit
  // each one. Empty for a brand-new report.
  const [initialReceipts, setInitialReceipts] = useState([]);

  const DRAFT_KEY = 'prohr_expense_draft';

  // Restore draft from localStorage on mount — but skip when editing an
  // existing report; we'll fetch its real data from the API below.
  const [header, setHeader] = useState(() => {
    if (editId) {
      return {
        claimPurpose: '', officeLocation: '', position: '',
        project: '', client: '', mileageRate: '', lessAdvancesPaid: 0,
      };
    }
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.lineItems?.length) {
          // Defer setting lineItems to after initial render. Backfill an
          // itemKey for any row saved before item↔receipt linking existed.
          const restored = parsed.lineItems.map((it) => ({
            ...it,
            itemKey: it.itemKey || crypto.randomUUID(),
          }));
          setTimeout(() => setLineItems(restored), 0);
        }
        return parsed.header || {
          claimPurpose: '', officeLocation: '', position: '',
          project: '', client: '', mileageRate: '', lessAdvancesPaid: 0,
        };
      }
    } catch {}
    return {
      claimPurpose: '', officeLocation: '', position: '',
      project: '', client: '', mileageRate: '', lessAdvancesPaid: 0,
    };
  });

  // Edit mode: load existing report + line items.
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await expenseApi.get(editId);
        if (cancelled) return;
        const r = res.report || {};
        setHeader({
          claimPurpose: r.claimPurpose || '',
          officeLocation: r.officeLocation || '',
          position: r.position || '',
          project: r.project || '',
          client: r.client || '',
          mileageRate: r.mileageRate || '',
          lessAdvancesPaid: r.lessAdvancesPaid || 0,
        });
        const rawItems = res.lineItems || [];
        const items = rawItems.map((it) => ({
          ...EMPTY_LINE,
          ...it,
          // Preserve the saved link; backfill for legacy rows so editing
          // and re-saving doesn't orphan their receipts.
          itemKey: it.itemKey || crypto.randomUUID(),
          currencyAmount: it.currencyAmount != null ? String(it.currencyAmount) : '',
          exchangeRate: it.exchangeRate != null ? String(it.exchangeRate) : '1',
        }));
        setLineItems(items.length ? items : [newLine()]);

        // Build the existing-receipts list for the uploader: each receipt
        // matched to its line item (by itemKey) so its details pre-fill
        // when the user opens it to review/fix.
        const built = (res.receipts || []).map((rc) => {
          const li = rawItems.find((l) => l.itemKey && l.itemKey === rc.itemKey) || null;
          const isPdf = /pdf/i.test(rc.mimeType || '') || /\.pdf$/i.test(rc.fileName || '');
          return {
            id: rc.itemKey || rc.rowKey,
            previewUrl: rc.url || null,
            isPdf,
            fileName: rc.fileName || '',
            receiptRowKey: rc.rowKey,
            form: li ? {
              date: li.date || '',
              expenseTypeId: li.expenseTypeId || '',
              description: li.description || '',
              currencyType: li.currencyType || 'SAR',
              currencyAmount: li.currencyAmount != null ? String(li.currencyAmount) : '',
              exchangeRate: li.exchangeRate != null ? String(li.exchangeRate) : '1',
              vendorName: li.vendorName || '',
              chargeAccount: li.chargeAccount || '',
              projectPermitted: li.projectPermitted === true,
              companyCardUsed: li.companyCardUsed === true,
            } : null,
          };
        });
        setInitialReceipts(built);
      } catch (err) {
        toast.error(`Failed to load draft: ${err.message}`);
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save draft to localStorage on changes
  useEffect(() => {
    // Only save if there's meaningful data
    const hasData = header.claimPurpose || lineItems.some((i) => i.date || i.currencyAmount);
    if (hasData && !reportId) {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ header, lineItems }));
      } catch {}
    }
  }, [header, lineItems, reportId]);

  useEffect(() => {
    // Skip in edit mode — the existing report already has position/rate
    // and we don't want to clobber what was saved.
    if (editId) return;
    if (user) {
      const pos = user.jobTitle || '';
      setHeader((h) => ({
        ...h,
        position: pos,
        mileageRate: MILEAGE_RATES[pos] || MILEAGE_RATES['Associate'] || '',
      }));
    }
  }, [user, editId]);

  useEffect(() => {
    async function loadTypes() {
      try {
        const res = await adminExpenseApi.listExpenseTypes();
        setExpenseTypes((res.types || []).filter((t) => t.isActive !== false));
      } catch { /* types not seeded yet */ }
    }
    async function loadPositions() {
      try {
        const res = await adminExpenseApi.listPositions();
        setPositions((res.positions || []).filter((p) => p.isActive !== false));
      } catch { /* positions not seeded yet */ }
    }
    async function loadProjects() {
      try {
        const res = await expenseApi.listProjects();
        setProjects(res.projects || []);
      } catch { /* Teamwork not reachable — Project stays free-text */ }
    }
    loadTypes();
    loadPositions();
    loadProjects();
  }, []);

  const handleHeaderChange = (field, value) => {
    setHeader((h) => ({ ...h, [field]: value }));
  };

  // ─── Line item helpers ───
  const updateLine = useCallback((index, field, value) => {
    setLineItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      // Auto-set exchange rate to 1 if SAR
      if (field === 'currencyType' && value === 'SAR') {
        copy[index].exchangeRate = '1';
      }
      return copy;
    });
  }, []);

  const addLine = () => setEditingItem({ index: 'new', draft: newLine() });

  const saveEditedItem = (draft) => {
    if (!editingItem) return;
    if (editingItem.index === 'new') {
      setLineItems((prev) => [...prev, draft]);
    } else {
      setLineItems((prev) => prev.map((it, i) => (i === editingItem.index ? draft : it)));
    }
    setEditingItem(null);
  };

  const removeLine = (index) => {
    setLineItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [newLine()];
    });
  };

  // round2 mirrors the backend's rounding boundary (api/my-expenses-line-items)
  // so client-displayed totals match what gets persisted.
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  // The current employee's home currency (from their admin-set Bank
  // Location), resolved once and reused everywhere the fee is computed.
  // Declared before getLineBreakdown/getFxNote (and anything memoized off
  // them) since it's read there — see the file-level note on hook/const
  // declaration order.
  const homeCurrency = homeCurrencyFor(user);

  // Mirrors the backend calc in api/my-expenses-line-items: baseSar is the
  // plain conversion, lines in a currency other than the employee's home
  // currency add a 3% foreign transaction fee on top, and sarAmount
  // (returned here) is the all-in total. Declared before any hooks/memos
  // that depend on it — see the file-level note on hook order.
  const getLineBreakdown = (item) => {
    const amt = parseFloat(item.currencyAmount) || 0;
    const rate = parseFloat(item.exchangeRate) || 1;
    const baseSar = round2(amt * rate);
    const isForeign = (item.currencyType || 'SAR').toUpperCase() !== homeCurrency;
    const foreignFeeSar = isForeign ? round2(baseSar * FOREIGN_TXN_FEE_RATE) : 0;
    const sarAmount = round2(baseSar + foreignFeeSar);
    return { baseSar, foreignFeeSar, sarAmount };
  };

  const getSarAmount = (item) => getLineBreakdown(item).sarAmount;

  // Compact FX note shown under the SAR total for foreign-currency lines in
  // the Review & Submit table — just the rate + the flat 3% fee,
  // highlighted, instead of spelling out the full base/fee/total arithmetic.
  const getFxNote = (item) => {
    if (!item.currencyType || item.currencyType.toUpperCase() === homeCurrency) return null;
    const rate = parseFloat(item.exchangeRate) || 1;
    return `@ ${rate.toFixed(4)} · +3%`;
  };

  const totalSAR = lineItems.reduce((sum, item) => sum + getSarAmount(item), 0);
  const totalForeignFeeSAR = lineItems.reduce((sum, item) => sum + getLineBreakdown(item).foreignFeeSar, 0);
  const netAmount = totalSAR - (parseFloat(header.lessAdvancesPaid) || 0);
  const validItems = lineItems.filter((i) => i.date && i.expenseTypeId && i.currencyAmount);
  // Partially-filled rows too — used when saving a draft so in-progress
  // receipt details aren't dropped just because they're not yet complete.
  const draftItems = lineItems.filter((i) => i.date || i.currencyAmount || i.expenseTypeId || i.description || i.vendorName);
  // Sort by date so Review & Submit, saved line numbers, approver views,
  // emails and exports all come out in date sequence.
  const byDate = (a, b) => String(a.date || '').localeCompare(String(b.date || ''));

  // Single guarded report-creation path. reportId is async React state, so
  // multiple callers (the 2s autosave, manual Save Draft, and the receipt
  // uploader) could each observe it as null and create a *separate* draft —
  // producing duplicate reports with the same details. A ref (updated
  // synchronously the instant a report is created) plus an in-flight-promise
  // guard ensure exactly one report is ever created, no matter how many
  // callers race.
  const reportIdRef = useRef(reportId);
  useEffect(() => { reportIdRef.current = reportId; }, [reportId]);
  const creatingReportRef = useRef(null);
  const ensureReport = useCallback(async () => {
    if (reportIdRef.current) return reportIdRef.current;
    if (creatingReportRef.current) return creatingReportRef.current; // reuse in-flight create
    const p = (async () => {
      const purpose = header.claimPurpose?.trim() || 'Untitled expense report';
      const res = await expenseApi.create({ ...header, claimPurpose: purpose });
      const rid = res.report?.rowKey || null;
      reportIdRef.current = rid;      // set synchronously so racing callers see it
      if (rid) setReportId(rid);
      return rid;
    })();
    creatingReportRef.current = p;
    try { return await p; }
    finally { creatingReportRef.current = null; }
  }, [header]);

  // Core persistence used by both the manual "Save Draft" button and the
  // silent autosave below. `opts.silent` skips the success toast and the
  // claimPurpose-required validation (autosave shouldn't nag the user for
  // a field they haven't gotten to yet — it just persists whatever exists).
  // Defined before the autosave block below, which lists it as a dependency.
  const persistDraft = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent && !header.claimPurpose?.trim()) {
      toast.error('Claim purpose is required');
      return false;
    }
    // Nothing worth persisting yet (autosave only — manual save always
    // proceeds so a purpose-only report can still be created).
    if (silent && !header.claimPurpose?.trim() && draftItems.length === 0) {
      return false;
    }
    // Reuse the shared, deduped creation path so autosave + manual save +
    // receipt upload can never each spin up their own draft.
    let rid = reportIdRef.current;
    if (rid) {
      await expenseApi.update(rid, header);
    } else {
      rid = await ensureReport();
    }
    // Save line items — always, even if empty, so deletions (down to
    // zero items) actually reach the DB instead of silently no-op'ing.
    // Drafts persist partially-filled rows too, not just fully-valid
    // ones, so in-progress receipt details survive a reopen.
    if (rid) {
      await expenseApi.saveLineItems(
        rid,
        [...draftItems].sort(byDate).map((item, i) => ({
          ...item,
          lineNumber: i + 1,
          sarAmount: getSarAmount(item),
        }))
      );
    }
    return rid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header, draftItems, ensureReport, homeCurrency]);

  // ─── Server autosave ───
  // Debounced ~2s after the last edit, this is the change that would have
  // saved the data described in the bug report: an in-progress expense
  // draft (header + receipts) now lives on the server, not just in memory.
  const [autosaveStatus, setAutosaveStatus] = useState('idle'); // idle | saved
  const snapshot = useCallback(
    () => JSON.stringify({ header, draftItems }),
    [header, draftItems]
  );
  const lastAutosavedRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const autosavingRef = useRef(false);

  const runAutosave = useCallback(async () => {
    // Never autosave while a manual save/submit is in flight, or while the
    // edit-mode initial load hasn't finished (loadingEdit would otherwise
    // let an autosave fire against not-yet-populated state and clobber it).
    if (saving || submitting || loadingEdit || autosavingRef.current) return;
    const current = snapshot();
    if (current === lastAutosavedRef.current) return; // nothing changed
    autosavingRef.current = true;
    try {
      const rid = await persistDraft({ silent: true });
      if (rid !== false) {
        lastAutosavedRef.current = current;
        setAutosaveStatus('saved');
      }
    } catch {
      // Silent — autosave failures shouldn't interrupt the user. The next
      // debounce tick (or manual Save Draft) will retry.
    } finally {
      autosavingRef.current = false;
    }
  }, [saving, submitting, loadingEdit, snapshot, persistDraft]);

  // Seed the "last saved" snapshot once the initial edit-mode load finishes,
  // so freshly-loaded (already-saved) data isn't immediately flagged dirty.
  const prevLoadingEditRef = useRef(loadingEdit);
  useEffect(() => {
    if (prevLoadingEditRef.current && !loadingEdit) {
      lastAutosavedRef.current = snapshot();
    }
    prevLoadingEditRef.current = loadingEdit;
  }, [loadingEdit, snapshot]);

  // Debounce: (re)start a 2s timer whenever the persisted snapshot actually
  // changes. Keyed off the serialized snapshot (not the header/draftItems
  // object identities, which change on every render) so unrelated re-renders
  // don't keep pushing the timer back and starving autosave.
  const snapshotValue = snapshot();
  useEffect(() => {
    if (loadingEdit) return; // don't autosave the initial edit-mode load
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => { runAutosave(); }, 2000);
    return () => clearTimeout(autosaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotValue, loadingEdit]);

  // Flush on tab-hide — best-effort. visibilitychange can safely await;
  // beforeunload can't, so we rely on the debounce + visibilitychange
  // having already saved by the time an actual unload happens.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) runAutosave();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [runAutosave]);

  // Register with the unsaved-work guard so a refresh/close/new-deploy
  // reload warns the user before this draft's not-yet-autosaved changes
  // are lost. Clears shortly after autosave catches up; stays false for a
  // genuinely empty, untouched new report (nothing to lose yet).
  const hasMeaningfulData = !!header.claimPurpose?.trim() || draftItems.length > 0;
  const isDirty = hasMeaningfulData && snapshotValue !== lastAutosavedRef.current;
  useUnsavedGuard({
    id: `expense-${reportId || 'new'}`,
    label: `Expense draft: ${header.claimPurpose || 'new'}`,
    href: reportId ? `/expenses/new?edit=${reportId}` : '/expenses/new',
    dirty: isDirty,
  });

  // Receipt uploader callback — runs when the user saves a receipt's
  // details. Upserts by itemKey: if a line item for this receipt already
  // exists (e.g. re-editing an existing receipt on a rejected report),
  // update it in place; otherwise slot into the first empty row or append.
  // Keying by itemKey is what prevents duplicate rows on re-edit.
  const handleReceiptLineItem = (item) => {
    const typeName = expenseTypes.find((t) => t.rowKey === item.expenseTypeId)?.displayName || item.expenseTypeId;
    const enriched = {
      ...item,
      expenseTypeName: typeName,
      exchangeRate: item.exchangeRate ? String(item.exchangeRate) : '1',
      currencyAmount: item.currencyAmount ? String(item.currencyAmount) : '',
    };
    setLineItems((prev) => {
      // Re-edit of an existing item → update in place.
      const keyIdx = item.itemKey ? prev.findIndex((l) => l.itemKey === item.itemKey) : -1;
      if (keyIdx >= 0) {
        const copy = [...prev];
        copy[keyIdx] = { ...copy[keyIdx], ...enriched };
        return copy;
      }
      // New receipt → first empty row, else append.
      const emptyIdx = prev.findIndex((l) => !l.date && !l.currencyAmount);
      if (emptyIdx >= 0) {
        const copy = [...prev];
        copy[emptyIdx] = enriched;
        return copy;
      }
      return [...prev, enriched];
    });
    toast.success('Receipt details saved');
  };

  // Called by ReceiptScanner when a receipt is removed — drops the line
  // item that was created from it (matched by itemKey) so a deleted
  // receipt's details don't get re-saved on the next save/submit.
  const handleReceiptRemoved = (itemKey) => {
    setLineItems((prev) => {
      const next = prev.filter((l) => l.itemKey !== itemKey);
      return next.length ? next : [newLine()];
    });
  };

  // Materialize a draft report on demand — called by ReceiptScanner
  // when the user drops their first receipt before clicking Save Draft.
  // Without this, the upload POSTs to /api/my/expenses/draft/receipts
  // and the backend 404s because no report row with rowKey="draft" exists.
  // Delegates to the shared, deduped ensureReport() so a receipt upload can't
  // create a second draft alongside one autosave just made (or is making).
  const ensureReportId = ensureReport;

  // ─── Save / Submit ───
  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const rid = await persistDraft();
      if (rid === false) return;
      toast.success('Draft saved');
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      lastAutosavedRef.current = snapshot();
      setAutosaveStatus('saved');
      if (step === 1) setStep(2);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!reportId) {
      // Save first
      await handleSaveDraft();
    }
    if (!reportId && !header.claimPurpose) return;

    setSubmitting(true);
    try {
      // Ensure latest items are saved — always call, even with zero valid
      // items, so a deletion that empties the report still propagates.
      // Read from the ref (set synchronously on create) so a just-created
      // report isn't missed while reportId state is still catching up.
      const rid = reportIdRef.current;
      if (rid) {
        await expenseApi.saveLineItems(
          rid,
          [...validItems].sort(byDate).map((item, i) => ({
            ...item,
            lineNumber: i + 1,
            sarAmount: getSarAmount(item),
          }))
        );
      }
      await expenseApi.submit(rid);
      toast.success('Expense report submitted for approval!');
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      lastAutosavedRef.current = snapshot();
      setAutosaveStatus('saved');
      navigate('/expenses');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeName = (typeId) => expenseTypes.find((t) => t.rowKey === typeId)?.displayName || typeId;

  return (
    <div>
      <button
        onClick={() => navigate('/expenses')}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors cursor-pointer"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Expenses
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{editId ? 'Edit Expense Report' : 'New Expense Report'}</h1>
          <p className="text-muted-foreground">
            {loadingEdit ? 'Loading draft…' : `Step ${step} of 2: ${step === 1 ? 'Report Details & Expenses' : 'Review & Submit'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {reportId && <Badge variant="success">Draft saved</Badge>}
          {reportId && isDirty && <Badge variant="default">Saving…</Badge>}
        </div>
      </div>

      {/* ═══════ STEP 1: Header + Inline Table ═══════ */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Header Card */}
          <Card>
            <CardHeader>
              <CardTitle>Report Details</CardTitle>
              <CardDescription>Basic information about this expense claim</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ClaimPurposeCombobox
                value={header.claimPurpose}
                onChange={(v) => handleHeaderChange('claimPurpose', v)}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Office Location"
                  value={header.officeLocation}
                  onChange={(e) => handleHeaderChange('officeLocation', e.target.value)}
                  placeholder="KSA or USA"
                />
                <ProjectCombobox
                  value={header.project}
                  onChange={(v) => handleHeaderChange('project', v)}
                  projects={projects}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Client"
                  value={header.client}
                  onChange={(e) => handleHeaderChange('client', e.target.value)}
                  placeholder="Client name"
                />
                <Select
                  label="Position"
                  value={header.position}
                  onChange={(e) => handleHeaderChange('position', e.target.value)}
                  options={[
                    { value: '', label: positions.length ? 'Select position…' : 'No positions configured' },
                    // Include the current value even if it isn't in the
                    // active list, so legacy reports with old/inactive
                    // positions still render correctly.
                    ...(header.position && !positions.some((p) => p.displayName === header.position)
                      ? [{ value: header.position, label: `${header.position} (legacy)` }]
                      : []),
                    ...positions.map((p) => ({ value: p.displayName, label: p.displayName })),
                  ]}
                />
              </div>
            </CardContent>
          </Card>

          {/* "AI coming soon" banner. The uploader below still works
              exactly like the existing scanner — employees upload receipts
              and fill in the details. What's deferred is the auto-extract
              UX, which is why the scanner component's AI-specific copy and
              error surfaces have been hidden for now. */}
          <Card className="border-l-4 border-l-primary/60">
            <CardContent className="flex items-start gap-3 py-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">AI receipt scanning</h3>
                  <Badge variant="info">Coming soon</Badge>
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Upload your receipts below and fill in the details. Soon,
                  KSA ProHR will read each receipt and fill the details in
                  automatically.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Receipt uploader (same component as before, AI branding removed) */}
          <ReceiptScanner
            reportId={reportId || 'draft'}
            ensureReport={ensureReportId}
            expenseTypes={expenseTypes}
            homeCurrency={homeCurrency}
            onLineItemAdded={handleReceiptLineItem}
            onReceiptRemoved={handleReceiptRemoved}
            onReceiptsChange={setReceiptCount}
            initialReceipts={initialReceipts}
          />

          {/* "No receipt?" escape hatch — small, secondary affordance under
              the uploader. Only shown before any items exist so it doesn't
              compete with the items table once data is filled in. */}
          {validItems.length === 0 && (
            <div className="flex items-center justify-center text-xs text-muted-foreground">
              <span>No receipt for this expense?&nbsp;</span>
              <button
                onClick={addLine}
                className="text-primary hover:underline cursor-pointer"
              >
                Add a manual line item
              </button>
            </div>
          )}

          {/* Inline Editable Expense Table — hidden until at least one
              filled line item exists. The receipt-upload flow is the
              primary path; the table is a summary of what's been added. */}
          {validItems.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Expense Items</CardTitle>
                  <CardDescription className="mt-1.5">{validItems.length} item{validItems.length !== 1 ? 's' : ''} &middot; {totalSAR.toFixed(2)} SAR total</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={addLine}>
                  <Plus className="h-4 w-4 mr-1.5" />
                  Manual row
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Editable table */}
              <div className="overflow-x-auto -mx-6 px-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[11px] text-muted-foreground uppercase tracking-wider">
                      <th className="pb-2 pr-2 w-8">#</th>
                      <th className="pb-2 pr-2 w-28">Date</th>
                      <th className="pb-2 pr-2 w-36">Type</th>
                      <th className="pb-2 pr-2 w-32">Vendor</th>
                      <th className="pb-2 pr-2 min-w-[180px]">Description</th>
                      <th className="pb-2 pr-2 w-20">Currency</th>
                      <th className="pb-2 pr-2 w-24">Amount</th>
                      <th className="pb-2 pr-2 w-20">Rate</th>
                      <th className="pb-2 pr-2 w-24 text-right">SAR</th>
                      <th className="pb-2 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, i) => {
                      const typeName = expenseTypes.find((t) => t.rowKey === item.expenseTypeId)?.displayName || '';
                      const empty = !item.date && !item.expenseTypeId && !item.currencyAmount && !item.description;
                      return (
                        <tr
                          key={i}
                          onClick={() => setEditingItem({ index: i, draft: { ...item } })}
                          className="border-b border-white/[0.03] group cursor-pointer hover:bg-accent/40 transition-colors"
                          title="Click to configure this item"
                        >
                          <td className="py-2.5 pr-2 text-muted-foreground text-xs">{i + 1}</td>
                          <td className="py-2.5 pr-2 text-xs">
                            {item.date || <span className="text-muted-foreground/40">—</span>}
                          </td>
                          <td className="py-2.5 pr-2 text-xs">
                            {typeName ? (
                              <span className="inline-flex items-center rounded-md bg-muted/50 px-2 py-0.5 text-xs font-medium">{typeName}</span>
                            ) : (
                              <span className="text-muted-foreground/40">—</span>
                            )}
                          </td>
                          <td className="py-2.5 pr-2 text-xs">
                            {item.vendorName ? (
                              <span className="truncate max-w-[140px] block">{item.vendorName}</span>
                            ) : (
                              <span className="text-muted-foreground/40">—</span>
                            )}
                          </td>
                          <td className="py-2.5 pr-2 text-xs">
                            {item.description ? (
                              <span className="truncate max-w-[220px] block">{item.description}</span>
                            ) : empty ? (
                              <span className="text-primary text-xs italic">Click to configure…</span>
                            ) : (
                              <span className="text-muted-foreground/40">—</span>
                            )}
                          </td>
                          <td className="py-2.5 pr-2 text-xs text-muted-foreground">{item.currencyType || '—'}</td>
                          <td className="py-2.5 pr-2 text-xs text-right font-mono">
                            {item.currencyAmount ? Number(item.currencyAmount).toFixed(2) : <span className="text-muted-foreground/40">—</span>}
                          </td>
                          <td className="py-2.5 pr-2 text-xs text-right font-mono text-muted-foreground">
                            {item.currencyType === 'SAR' ? '—' : (item.exchangeRate || '—')}
                          </td>
                          <td className="py-2.5 pr-2 text-right">
                            {(() => {
                              const { foreignFeeSar, sarAmount } = getLineBreakdown(item);
                              return (
                                <>
                                  <div className="font-mono text-xs font-semibold text-profun-cyan">
                                    {sarAmount.toFixed(2)}
                                  </div>
                                  {foreignFeeSar > 0 && (
                                    <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                                      incl. 3% FX fee: {foreignFeeSar.toFixed(2)}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </td>
                          <td className="py-2.5">
                            <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                              <span className="p-1 rounded text-muted-foreground/60" title="Edit (click row)">
                                <Pencil className="h-3.5 w-3.5" />
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); removeLine(i); }}
                                className="p-1 rounded text-muted-foreground/30 hover:text-profun-pink hover:bg-profun-pink/10 transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {totalForeignFeeSAR > 0 && (
                      <tr>
                        <td colSpan={8} className="pt-2 text-right text-[11px] text-muted-foreground pr-2">
                          Foreign txn fees (3%):
                        </td>
                        <td className="pt-2 text-right font-mono text-[11px] text-muted-foreground">
                          {totalForeignFeeSAR.toFixed(2)}
                        </td>
                        <td />
                      </tr>
                    )}
                    <tr className="border-t border-border">
                      <td colSpan={8} className="py-3 text-right text-xs font-semibold text-muted-foreground pr-2">
                        Total (SAR):
                      </td>
                      <td className="py-3 text-right font-mono font-bold text-profun-cyan">
                        {totalSAR.toFixed(2)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Optional extras — collapsible */}
              <div className="mt-6 pt-4 border-t border-border">
                <button
                  onClick={() => setShowOptional(!showOptional)}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  {showOptional ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  Optional: Mileage Rate, Advances, Charge Account
                </button>
                {showOptional && (
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <Input
                      label="Mileage Rate"
                      value={header.mileageRate}
                      onChange={(e) => handleHeaderChange('mileageRate', e.target.value)}
                      placeholder="Auto-filled by position"
                    />
                    <Input
                      label="Less Advances Paid (SAR)"
                      type="number"
                      step="0.01"
                      value={header.lessAdvancesPaid}
                      onChange={(e) => handleHeaderChange('lessAdvancesPaid', e.target.value)}
                      placeholder="0.00"
                    />
                    <div className="flex flex-col justify-end">
                      <div className="text-xs text-muted-foreground mb-1">Net Amount</div>
                      <div className="font-mono font-bold text-profun-cyan text-lg">{netAmount.toFixed(2)} SAR</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-border">
                <Button variant="secondary" onClick={handleSaveDraft} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Draft'}
                </Button>
                <Button onClick={() => { handleSaveDraft().then(() => setStep(2)); }} disabled={saving || validItems.length === 0}>
                  Review & Submit
                </Button>
              </div>
            </CardContent>
          </Card>
          )}
        </div>
      )}

      {/* ═══════ STEP 2: Review & Submit ═══════ */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Review & Submit</CardTitle>
            <CardDescription>Verify everything is correct before submitting for approval</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Header summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Purpose</div>
                <div className="font-medium">{header.claimPurpose}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Location</div>
                <div>{header.officeLocation || 'N/A'}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Project</div>
                <div>{header.project || 'N/A'}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Client</div>
                <div>{header.client || 'N/A'}</div>
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Read-only table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] text-muted-foreground uppercase tracking-wider">
                    <th className="pb-2 pr-3">#</th>
                    <th className="pb-2 pr-3">Date</th>
                    <th className="pb-2 pr-3">Type</th>
                    <th className="pb-2 pr-3">Vendor</th>
                    <th className="pb-2 pr-3">Description</th>
                    <th className="pb-2 pr-3">Currency</th>
                    <th className="pb-2 pr-3 text-right">Amount</th>
                    <th className="pb-2 pr-3 text-right">Rate</th>
                    <th className="pb-2 text-right">SAR</th>
                    <th className="pb-2 pl-2 w-6" />
                  </tr>
                </thead>
                <tbody>
                  {[...validItems].sort(byDate).map((item, i) => {
                    const idx = lineItems.findIndex((l) => (item.itemKey && l.itemKey === item.itemKey) || l === item);
                    return (
                      <tr
                        key={item.itemKey || i}
                        onClick={() => setEditingItem({ index: idx, draft: { ...item } })}
                        className="border-b border-border/50 group cursor-pointer hover:bg-accent/40 transition-colors"
                        title="Click to edit this item"
                      >
                        <td className="py-2.5 pr-3 text-muted-foreground">{i + 1}</td>
                        <td className="py-2.5 pr-3">{item.date}</td>
                        <td className="py-2.5 pr-3">
                          <Badge variant="default">{getTypeName(item.expenseTypeId)}</Badge>
                        </td>
                        <td className="py-2.5 pr-3">{item.vendorName || <span className="text-muted-foreground/60">—</span>}</td>
                        <td className="py-2.5 pr-3 max-w-[250px]">{item.description || <span className="text-profun-pink/60 italic">No description</span>}</td>
                        <td className="py-2.5 pr-3 text-muted-foreground">{item.currencyType}</td>
                        <td className="py-2.5 pr-3 text-right font-mono">{parseFloat(item.currencyAmount || 0).toFixed(2)}</td>
                        <td className="py-2.5 pr-3 text-right font-mono text-muted-foreground">{parseFloat(item.exchangeRate || 1).toFixed(4)}</td>
                        <td className="py-2.5 text-right">
                          {(() => {
                            const { sarAmount } = getLineBreakdown(item);
                            const note = getFxNote(item);
                            return (
                              <>
                                <div className="font-mono font-semibold text-profun-cyan">{sarAmount.toFixed(2)}</div>
                                {note && (
                                  <div className="mt-0.5 inline-flex items-center rounded-full bg-profun-cyan/10 px-1.5 py-0.5 text-[10px] font-medium text-profun-cyan whitespace-nowrap">
                                    {note}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td className="py-2.5 pl-2 w-6">
                          <Pencil className="h-3.5 w-3.5 text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="bg-muted/50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Claimed (SAR)</span>
                <span className="font-mono font-semibold">{totalSAR.toFixed(2)}</span>
              </div>
              {totalForeignFeeSAR > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Includes foreign transaction fees (3%)</span>
                  <span className="font-mono">{totalForeignFeeSAR.toFixed(2)}</span>
                </div>
              )}
              {parseFloat(header.lessAdvancesPaid) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Less Advances Paid</span>
                  <span className="font-mono">-{parseFloat(header.lessAdvancesPaid).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold pt-2 border-t border-border">
                <span>Net Amount (SAR)</span>
                <span className="text-profun-cyan font-mono">{netAmount.toFixed(2)}</span>
              </div>
            </div>

            {/* Confirmation */}
            <div className="bg-profun-blue/5 border border-profun-blue/10 rounded-xl p-4 text-sm text-muted-foreground">
              I confirm that the above expenses were incurred wholly and exclusively on behalf of Company business needs and request reimbursement.
            </div>

            <div className="flex justify-between items-center pt-4">
              <Button variant="secondary" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Edit
              </Button>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => navigate('/expenses')}>
                  <Save className="h-4 w-4 mr-2" />
                  Save as Draft
                </Button>
                <Button onClick={handleSubmit} disabled={submitting || validItems.length === 0}>
                  <Send className="h-4 w-4 mr-2" />
                  {submitting ? 'Submitting...' : 'Submit for Approval'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {editingItem && (
        <LineItemEditModal
          draft={editingItem.draft}
          isNew={editingItem.index === 'new'}
          expenseTypes={expenseTypes}
          homeCurrency={homeCurrency}
          onClose={() => setEditingItem(null)}
          onSave={saveEditedItem}
        />
      )}
    </div>
  );
}

// Single-item edit dialog. Mounted whenever editingItem !== null on the
// parent, with a copy of the line item as `draft`. Saving merges the
// draft back into lineItems[index]; cancel discards. New items only
// land in the table after Save (so an empty placeholder doesn't sit
// around with no data).
function LineItemEditModal({ draft, isNew, expenseTypes, homeCurrency, onClose, onSave }) {
  const [d, setD] = useState(draft);
  const set = (field, value) => setD((p) => ({ ...p, [field]: value }));

  // Auto-fill the exchange rate (-> SAR) for this line's date. Stays
  // editable; an existing item's saved rate is left untouched on open.
  const hasExistingRate = !isNew && draft.currencyType !== 'SAR' && !!draft.exchangeRate && draft.exchangeRate !== '1';
  const fx = useFxRate({
    currency: d.currencyType,
    date: d.date,
    hasExistingRate,
    onRate: (rate) => set('exchangeRate', String(rate)),
  });

  const handleSave = () => {
    if (!d.date) return;
    if (!d.expenseTypeId) return;
    if (!d.currencyAmount || Number(d.currencyAmount) <= 0) return;
    onSave({ ...d });
  };

  const ready = !!d.date && !!d.expenseTypeId && !!d.currencyAmount && Number(d.currencyAmount) > 0;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isNew ? 'Add Line Item' : 'Edit Line Item'}
      width="540px"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={!ready}>
            <Save className="h-4 w-4 mr-2" /> {isNew ? 'Add Item' : 'Save Changes'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Date *"
            type="date"
            value={d.date}
            onChange={(e) => set('date', e.target.value)}
          />
          <Select
            label="Type *"
            value={d.expenseTypeId}
            onChange={(e) => set('expenseTypeId', e.target.value)}
            options={[
              { value: '', label: 'Select type…' },
              ...expenseTypes.map((t) => ({ value: t.rowKey, label: t.displayName })),
            ]}
          />
        </div>

        <Input
          label="Vendor / Merchant"
          value={d.vendorName || ''}
          onChange={(e) => set('vendorName', e.target.value)}
          placeholder="e.g. Uber, Marriott, Emirates"
        />

        <Input
          label="Description"
          value={d.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="e.g. Lunch with client"
        />

        <div className="grid grid-cols-3 gap-3">
          <CurrencyCombobox
            value={d.currencyType}
            onChange={(code) => setD((p) => ({ ...p, currencyType: code, exchangeRate: code === 'SAR' ? '1' : p.exchangeRate }))}
          />
          <Input
            label="Amount *"
            type="number"
            step="0.01"
            value={d.currencyAmount}
            onChange={(e) => set('currencyAmount', e.target.value)}
            placeholder="0.00"
          />
          <Input
            label={fx.loading ? 'Exchange Rate · fetching…' : fx.error ? 'Exchange Rate · enter manually' : 'Exchange Rate'}
            type="number"
            step="0.0001"
            value={d.exchangeRate}
            onChange={(e) => set('exchangeRate', e.target.value)}
            disabled={d.currencyType === 'SAR'}
          />
        </div>

        {(() => {
          const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
          const amt = Number(d.currencyAmount) || 0;
          // Non-SAR lines carry a real exchange rate; SAR lines are 1:1 —
          // this drives the SAR conversion and is unrelated to the fee.
          const isNonSarCurrency = d.currencyType !== 'SAR';
          // Foreign = different from the employee's home currency — this
          // is what determines the 3% fee, independent of the SAR conversion.
          const isForeign = (d.currencyType || 'SAR').toUpperCase() !== homeCurrency;
          const rate = isNonSarCurrency ? (Number(d.exchangeRate) || 0) : 1;
          const baseSar = round2(amt * rate);
          const foreignFeeSar = isForeign ? round2(baseSar * FOREIGN_TXN_FEE_RATE) : 0;
          const total = round2(baseSar + foreignFeeSar);

          if (!isForeign) {
            return (
              <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total in SAR</span>
                <span className="font-mono font-bold text-profun-cyan text-base">{total.toFixed(2)}</span>
              </div>
            );
          }

          return (
            <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-1.5 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Converted</span>
                <span className="font-mono">{baseSar.toFixed(2)} SAR</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Foreign txn fee (3%)</span>
                <span className="font-mono">{foreignFeeSar.toFixed(2)} SAR</span>
              </div>
              <div className="flex items-center justify-between pt-1.5 border-t border-border/60">
                <span className="text-muted-foreground font-medium">Total</span>
                <span className="font-mono font-bold text-profun-cyan text-base">{total.toFixed(2)} SAR</span>
              </div>
            </div>
          );
        })()}
      </div>
    </Modal>
  );
}

// Project combobox — free-text input with a dropdown of Teamwork project
// names (fetched via /api/my/projects). Filters as the user types and
// lets them pick a project, but still allows free text so it degrades to
// the old plain field when Teamwork isn't configured or a project isn't
// listed. Stores the project NAME in header.project (unchanged shape).
function ProjectCombobox({ value, onChange, projects = [] }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef(null);

  const names = projects.map((p) => p.name).filter(Boolean);
  const matches = names
    .filter((n) => !value || n.toLowerCase().includes(value.toLowerCase()))
    .slice(0, 50);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { setOpen(true); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIndex >= 0 && matches[activeIndex]) { e.preventDefault(); pick(matches[activeIndex]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const hasProjects = names.length > 0;

  return (
    <div ref={wrapRef} className="relative flex flex-col gap-1.5">
      <label className="text-[13px] font-medium text-muted-foreground">Project</label>
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActiveIndex(-1); }}
        onFocus={() => hasProjects && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={hasProjects ? 'Search projects or type a name' : 'Project name or N/A'}
        className="flex h-10 w-full rounded-lg border border-border bg-card px-3.5 py-2 text-sm text-foreground shadow-sm ring-offset-background transition-all duration-150 placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:border-primary/50"
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
          {matches.map((s, i) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActiveIndex(i)}
              className={`block w-full px-3.5 py-2 text-left text-sm transition-colors ${
                i === activeIndex ? 'bg-primary/10 text-foreground' : 'text-foreground hover:bg-accent'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Claim Purpose combobox — free-text input with a dropdown of common
// purposes (Project, Client, Travel, Monthly Claim). Filters as the user
// types; clicking a suggestion fills the field. Users can still type
// anything they want.
const CLAIM_PURPOSE_SUGGESTIONS = ['Project', 'Client', 'Travel', 'Monthly Claim'];

function ClaimPurposeCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef(null);

  const matches = CLAIM_PURPOSE_SUGGESTIONS.filter((s) =>
    !value || s.toLowerCase().includes(value.toLowerCase())
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0 && matches[activeIndex]) {
      e.preventDefault();
      pick(matches[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative flex flex-col gap-1.5">
      <label className="text-[13px] font-medium text-muted-foreground">Claim Purpose *</label>
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActiveIndex(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="e.g. Monthly Claim, Project, Client, Travel"
        className="flex h-10 w-full rounded-lg border border-border bg-card px-3.5 py-2 text-sm text-foreground shadow-sm ring-offset-background transition-all duration-150 placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:border-primary/50"
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-border bg-card shadow-lg">
          {matches.map((s, i) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActiveIndex(i)}
              className={`block w-full px-3.5 py-2 text-left text-sm transition-colors ${
                i === activeIndex ? 'bg-primary/10 text-foreground' : 'text-foreground hover:bg-accent'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
